/**
 * Age groups, pure (batch-29, item R1; plan §3.3). An event either uses a set of age groups or it does not. Only each
 * group's starting age is stored: a group runs up to the next group's start minus one and the last is open-ended, so
 * gaps and overlaps cannot exist. Keys are stable ids, so renaming a group never touches an earlier booking. A group
 * stored with `off: true` keeps its row and its ages but is not offered to guests (queue item BG3); the oldest never is off.
 */
export interface AgeGroup { key: string; name: string; from: number; crib?: boolean; off?: boolean }
/** A group with its last age filled in; `to` is null for the open-ended last group. */
export interface AgeGroupRange extends AgeGroup { to: number | null }

/** The set an event starts with, and what "Reset to defaults" restores. The database carries the same list. */
export const STANDARD_AGE_GROUPS: readonly AgeGroup[] = [
  { key: 'infant', name: 'Infants', from: 0, crib: true },
  { key: 'toddler', name: 'Toddlers', from: 3 },
  { key: 'child', name: 'Children', from: 8 },
  { key: 'teen', name: 'Teens', from: 13 },
  { key: 'adult', name: 'Adults', from: 18 },
];

/** Age groups off: two built-in groups. Neither uses a crib, so no crib is ever added on such an event. */
export const BUILT_IN_AGE_GROUPS: readonly AgeGroup[] = [
  { key: 'child', name: 'Children', from: 0 },
  { key: 'adult', name: 'Adults', from: 18 },
];

const KEY = /^[a-z][a-z0-9_]{0,23}$/;

/** Why a set cannot be saved, or null when it can. One rule, also enforced by the event's trigger (item R2). */
export function ageGroupsProblem(groups: readonly AgeGroup[]): 'count' | 'key' | 'name' | 'start' | 'order' | 'crib' | 'offered' | null {
  if (groups.length < 2 || groups.length > 8) return 'count';
  if (groups.some((g) => !KEY.test(g.key)) || new Set(groups.map((g) => g.key)).size !== groups.length) return 'key';
  if (groups.some((g) => g.name.trim().length < 1 || g.name.trim().length > 30)) return 'name';
  if (groups.some((g) => !Number.isInteger(g.from) || g.from < 0 || g.from > 120) || groups[0]!.from !== 0) return 'start';
  if (groups.some((g, i) => i > 0 && g.from <= groups[i - 1]!.from)) return 'order';
  if (groups.filter((g) => g.crib).length > 1) return 'crib';
  if (groups[groups.length - 1]!.off === true) return 'offered';
  return null;
}

/** The groups an event uses, youngest first, each with its last age. `null` (age groups off) gives the two built-in groups. */
export function effectiveAgeGroups(stored: readonly AgeGroup[] | null | undefined): AgeGroupRange[] {
  const set = stored && stored.length ? stored : BUILT_IN_AGE_GROUPS;
  return set.map((g, i) => ({ ...g, to: i + 1 < set.length ? set[i + 1]!.from - 1 : null }));
}

/** The key of the group that sleeps in cribs, or null when the event has none. */
export const cribGroupKey = (groups: readonly AgeGroup[]): string | null => groups.find((g) => g.crib)?.key ?? null;

/** "Ages 0 to 2", "Age 12", "Ages 18 and up". */
export function ageRangeText(g: AgeGroupRange): string {
  if (g.to === null) return `Ages ${g.from} and up`;
  return g.to === g.from ? `Age ${g.from}` : `Ages ${g.from} to ${g.to}`;
}
